import * as React from 'react';
import { useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AgentChatActions, useAgentChat } from '../../../components/agent_chat/useAgentChat';
import { AgentActionOutcome } from '../../../types/agentHarness';
import useMountEffect from '../../../hooks/useMountEffect';
import { withCurrentUser } from '../../../hooks/withCurrentUser';
import { withUserType } from '../../../components/withUserType';
import {
	cancelAgentTurn,
	createAgentConversation,
	exchangeAgentLogin,
	fetchAgentIdentity,
	fetchAgentModels,
	resolveAgentApproval,
	fetchAgentActions,
	fetchAgentAction,
	reconcileAgentAction,
	sendAgentMessage,
	setAgentConversationModel,
	setAgentThinkingLevel,
	downloadAgentFile,
	uploadAgentFile,
} from '../../../thunks/agent_harness_thunks';
import { cn } from '../../../utils/ClassNamesUtils';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { AGENT_CHAT_FEATURE_FLAG } from '../../../utils/EnvConfigUtils';
import { OscarRoleProvider } from '../../context/OscarRoleContext';
import {
	readStoredConversationId,
	sessionIdentityMatches,
	storeConversationId,
	storeSessionIdentity,
	takePanelOpenIntent,
} from '../../utils/OscarConversationStorage';
import OscarConnectionState, { oscarComposerPlaceholder } from '../shared/OscarConnectionState';
import OscarTranscript from '../oscar_fullscreen/OscarTranscript';
import OscarEdgeTab from '../oscar_launcher/OscarEdgeTab';
import OscarPanel from '../oscar_panel/OscarPanel';
import { OSCAR_FULL_MEASURE } from '../../constants/AIAgentConstants';
import './OscarDock.less';

interface AgentActionProps {
	exchangeLogin?: AgentChatActions['exchangeLogin'];
	fetchModels?: AgentChatActions['fetchModels'];
	fetchIdentity?: AgentChatActions['fetchIdentity'];
	createConversation?: AgentChatActions['createConversation'];
	sendMessage?: AgentChatActions['sendMessage'];
	cancelTurn?: AgentChatActions['cancelTurn'];
	setConversationModel?: AgentChatActions['setConversationModel'];
	setThinkingLevel?: AgentChatActions['setThinkingLevel'];
	resolveApproval?: AgentChatActions['resolveApproval'];
	fetchActions?: AgentChatActions['fetchActions'];
	fetchAction?: AgentChatActions['fetchAction'];
	reconcileAction?: AgentChatActions['reconcileAction'];
	uploadFile?: AgentChatActions['uploadFile'];
	/** Not part of the chat hook — passed straight to the transcript's file cards. */
	downloadFile?: (url: string) => Promise<Blob>;
}

interface OscarDockProps extends AgentActionProps {
	/** Greeting name, e.g. "Hello Arnav,". */
	userName: string;
	/** Injected by `withUserType` — `state.session.userType`. */
	userType?: string;
	/** Injected by `withCurrentUser` — `state.session.currentUser`. */
	currentUser?: { email?: string };
}

interface OscarDockPanelProps extends AgentActionProps {
	userName: string;
	userType: string;
	/** Scopes the resumable conversation to this account. */
	userEmail: string;
	/** Slides the drawer out, then unmounts it. */
	onClose: () => void;
	/** Unmounts at once, for leaving the page rather than closing the drawer. */
	onCloseNow: () => void;
}

/**
 * No starter suggestions and no context bar: both were sample copy from the
 * designs. Nothing real produces suggestions yet, and the context bar would
 * claim a record is being shared when nothing is — `createConversation` and
 * `sendMessage` carry no record, so the agent has no idea what you are looking
 * at. `OscarPanel` still accepts both; wire them when the harness can take a
 * context payload.
 *
 * The open panel, split out **on purpose**: `useAgentChat` exchanges a login,
 * creates a conversation and opens an EventSource on mount. The dock lives in
 * the top bar of every page, so running the hook there would open a stream and
 * mint a throwaway conversation on every page load whether or not anyone opened
 * the agent. Mounting this only while open ties the connection to intent, and
 * the hook's unmount cleanup closes the stream again on close.
 */
const OscarDockPanel = ({
	userName,
	userType,
	userEmail,
	onClose,
	onCloseNow,
	...actionProps
}: OscarDockPanelProps) => {
	const history = useHistory();

	const actions: AgentChatActions = useMemo(
		() => ({
			exchangeLogin: actionProps.exchangeLogin,
			fetchModels: actionProps.fetchModels,
			fetchIdentity: actionProps.fetchIdentity,
			createConversation: actionProps.createConversation,
			sendMessage: actionProps.sendMessage,
			cancelTurn: actionProps.cancelTurn,
			setConversationModel: actionProps.setConversationModel,
			setThinkingLevel: actionProps.setThinkingLevel,
			resolveApproval: actionProps.resolveApproval,
			fetchActions: actionProps.fetchActions,
			fetchAction: actionProps.fetchAction,
			reconcileAction: actionProps.reconcileAction,
			uploadFile: actionProps.uploadFile,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);
	// Read once on mount so the id is stable for this panel's lifetime.
	// Bound to the signed-in account: the harness cookie and sessionStorage both
	// outlive an OpenWrench sign-out, so an unbound id would hand the next user
	// the previous user's conversation.
	const [initialConversationId] = useState(() => readStoredConversationId(userEmail));
	const chat = useAgentChat(actions, {
		conversationId: initialConversationId,
		onConversationId: (conversationId) => storeConversationId(userEmail, conversationId),
		// A live harness cookie is not proof of whose it is: it outlives sign-out
		// and a workspace switch. Reuse only when the harness agrees on the person
		// and our own record agrees on the workspace.
		canReuseSession: (harnessEmail, harnessRole) =>
			harnessEmail === userEmail &&
			harnessRole === userType &&
			sessionIdentityMatches(userEmail, userType),
		onSessionEstablished: () => storeSessionIdentity(userEmail, userType),
	});

	const hasContent = chat.entries.length > 0 || Object.keys(chat.unsentMessages).length > 0;
	// Show the conversation area, not the greeting, whenever content is coming.
	//
	// Two cases, both of which used to flash "Hello …, How can I help you today?"
	// and then replace it a beat later:
	//  - opening onto a stored conversation, whose transcript is still replaying;
	//  - a retry that rebuilds the conversation, which clears the transcript
	//    before appending the message again.
	// A first-ever open has no stored id, so the greeting still appears at once.
	const showTranscript = hasContent || (!!initialConversationId && chat.status === 'connecting');

	return (
		<OscarPanel
			resizable
			className="h-full"
			userName={userName}
			onSendMessage={(message) => chat.sendMessage(message)}
			composerDisabled={chat.status !== 'ready'}
			composerPlaceholder={oscarComposerPlaceholder(chat.status)}
			// Sits above the composer whether or not there is a conversation yet, so
			// a failure during boot is visible instead of leaving a dead composer
			// under the greeting.
			statusStrip={
				<OscarConnectionState status={chat.status} error={chat.errorInfo} onRetry={chat.retry} />
			}
			sending={chat.busy}
			onCancelMessage={chat.stopTurn}
			onNewChat={chat.newConversation}
			// Close the dock on the way out, or its portal would sit on top of the
			// fullscreen route.
			onExpand={() => {
				onCloseNow();
				// The marker is what lets fullscreen know it can go back: arriving
				// from a bookmark or an external link has no in-app entry to return
				// to, and `history.length` cannot tell those apart.
				history.push(`/${userType}/agent`, { fromDock: true });
			}}
			onClose={onClose}
		>
			{showTranscript ? (
				<OscarTranscript
					// The reader sets the drawer's width by dragging it, so the text takes
					// whatever room they give it rather than centring in a capped column.
					measure={OSCAR_FULL_MEASURE}
					entries={chat.entries}
					busy={chat.busy}
					turnStartedAt={chat.turnStartedAt}
					userInitial={(userName || '?').charAt(0).toUpperCase()}
					onResolveApproval={chat.resolveApproval}
					onReconcileAction={chat.reconcileAction}
					unsentMessages={chat.unsentMessages}
					// While the connection strip is showing its own Try again, do not
					// offer a second one — they run the same action.
					onDownloadFile={actionProps.downloadFile}
					onRetrySend={chat.status === 'error' ? undefined : chat.retrySend}
				/>
			) : null}
		</OscarPanel>
	);
};

/**
 * Oscar entry point: a tab on the bottom-right edge of the screen that opens the
 * drawer. Both are portalled to <body>, so nothing renders where the dock is
 * mounted and no ancestor's overflow, transform or stacking context can clip
 * them. The tab is hidden while the drawer is open.
 *
 * The drawer slides in on open and out on close. Closing keeps it mounted until
 * its exit animation ends, so the connection closes once it is off screen.
 *
 * Gated on the `agent-chat` PostHog flag, the same one that reveals the menu
 * items in `EnvConfigUtils`.
 */
const OscarDockBase = ({ userName, userType, currentUser, ...actionProps }: OscarDockProps) => {
	// Reopens itself once after a shrink; the intent is cleared as it is read.
	const [open, setOpen] = useState(takePanelOpenIntent);
	const [closing, setClosing] = useState(false);
	// Read into state and subscribed, not read during render. `session_thunks`
	// renders the dock before `ThirdPartyService.boot` calls `posthog.identify`,
	// so the first evaluation is anonymous — and if that came back false, nothing
	// re-rendered when the real flags landed. An enabled user simply never saw the
	// launcher until some unrelated update happened to refresh this component.
	const [enabled, setEnabled] = useState(() => !!posthog.isFeatureEnabled(AGENT_CHAT_FEATURE_FLAG));
	const userEmail = nullSafeGetOrElse('email', currentUser, '');

	useMountEffect(() => {
		setEnabled(!!posthog.isFeatureEnabled(AGENT_CHAT_FEATURE_FLAG));
		return posthog.onFeatureFlags(() => {
			setEnabled(!!posthog.isFeatureEnabled(AGENT_CHAT_FEATURE_FLAG));
		});
	});

	if (!enabled) {
		return null;
	}

	const closeNow = () => {
		setOpen(false);
		setClosing(false);
	};

	return createPortal(
		<OscarRoleProvider userType={userType}>
			{open ? (
				<div
					className={cn(
						// Full width on small screens, where the panel's 420px minimum would
						// run off the edge; anchored right from `sm` up.
						'fixed inset-y-0 left-0 right-0 z-[999] sm:left-auto print:hidden',
						closing ? 'oscar-dock-drawer--closing' : 'oscar-dock-drawer'
					)}
					onAnimationEnd={(event) => {
						// Animations inside the drawer bubble up here too.
						if (closing && event.target === event.currentTarget) {
							closeNow();
						}
					}}
				>
					<OscarDockPanel
						userName={userName}
						userType={userType}
						userEmail={userEmail}
						onClose={() => setClosing(true)}
						onCloseNow={closeNow}
						{...actionProps}
					/>
				</div>
			) : (
				<OscarEdgeTab className="print:!hidden" onClick={() => setOpen(true)} />
			)}
		</OscarRoleProvider>,
		document.body
	);
};

const mapDispatchToProps = (dispatch: any, ownProps: { userType: string }) => ({
	exchangeLogin: () => dispatch(exchangeAgentLogin(ownProps.userType)),
	fetchModels: () => dispatch(fetchAgentModels()),
	fetchIdentity: () => dispatch(fetchAgentIdentity()),
	createConversation: (model?: string, thinkingLevel?: string) =>
		dispatch(createAgentConversation(model, thinkingLevel)),
	sendMessage: (conversationId: string, messageId: string, text: string) =>
		dispatch(sendAgentMessage(conversationId, messageId, text)),
	cancelTurn: (conversationId: string) => dispatch(cancelAgentTurn(conversationId)),
	setConversationModel: (conversationId: string, model: string) =>
		dispatch(setAgentConversationModel(conversationId, model)),
	setThinkingLevel: (conversationId: string, thinkingLevel: string) =>
		dispatch(setAgentThinkingLevel(conversationId, thinkingLevel)),
	resolveApproval: (approvalId: string, approved: boolean) =>
		dispatch(resolveAgentApproval(approvalId, approved)),
	fetchActions: () => dispatch(fetchAgentActions()),
	fetchAction: (actionId: string) => dispatch(fetchAgentAction(actionId)),
	reconcileAction: (actionId: string, outcome: AgentActionOutcome, evidence: string) =>
		dispatch(reconcileAgentAction(actionId, outcome, evidence)),
	uploadFile: (file: File) => dispatch(uploadAgentFile(file)),
	downloadFile: (url: string) => dispatch(downloadAgentFile(url, ownProps.userType)),
});

export const OscarDock = withUserType(
	withCurrentUser(connect(null, mapDispatchToProps)(OscarDockBase))
);

export default OscarDock;
